// Todas las promos que le aplican a un teléfono del portal. Suma las de venta de su fila, las de
// crédito que lo nombran y las de crédito de toda la tienda. Puro.
import { BadgePercent, Tag } from 'lucide-react';
import type { PortalCatalogRow, PortalSalePromotion } from '@/api/services/retail-wizard/contract';
import { sureKeepFinances } from '@/domains/sales/wizard/cardBenefits';
import {
    creditPromotionRows,
    type CreditPromotionRow,
} from '@/domains/sales/wizard/creditPromotions';
import { effectChips } from '@/domains/sales/wizard/promotionDetail';
import type { BenefitBubble } from '@/domains/sales/wizard/promotionSlots';
import type { ScopeWidePromotion } from '@/domains/sales/wizard/types';

export interface PhonePromotions {
    /** De venta, en orden alfabético. */
    sale: PortalSalePromotion[];
    /** De crédito del SKU y de toda la tienda, sin repetir y en orden alfabético. */
    credit: CreditPromotionRow[];
    /** La fuente de las de venta no respondió. */
    saleUnavailable: boolean;
    /** El silo marca promo pero no llegó ninguna con detalle. */
    undetailed: boolean;
}

type PromotedRow = Pick<
    PortalCatalogRow,
    | 'hasPromo'
    | 'promotions'
    | 'salePromotions'
    | 'eligibleFinanciers'
    | 'financeable'
    | 'affordableOnCredit'
>;

/** Las de crédito solo cuentan si SureKeep financia el equipo. */
export function phonePromotions(
    item: PromotedRow,
    scopeWide: readonly ScopeWidePromotion[] = [],
): PhonePromotions {
    const sale = [...(item.salePromotions ?? [])].sort((a, b) =>
        a.name.localeCompare(b.name, 'es-MX'),
    );
    const credit = sureKeepFinances(item) ? creditPromotionRows(scopeWide, [item], undefined) : [];
    return {
        sale,
        credit,
        saleUnavailable: item.salePromotions === null,
        undetailed: item.hasPromo && sale.length === 0 && credit.length === 0,
    };
}

export function promotionCount(promotions: PhonePromotions): number {
    return promotions.sale.length + promotions.credit.length;
}

/** El beneficio de una promo de venta en una línea; si no lo resume, su nombre. */
export function saleBenefitText(promotion: PortalSalePromotion): string {
    return promotion.benefitSummary.trim() === '' ? promotion.name : promotion.benefitSummary;
}

/** Burbujas del wizard: verdes las de venta y moradas las de crédito, en ese orden. */
export function promotionBubbles(promotions: PhonePromotions): BenefitBubble[] {
    const sale = promotions.sale.map((promotion) => ({
        key: `sale-${promotion.promotionId}`,
        label: saleBenefitText(promotion),
        title: `${promotion.name}: ${saleBenefitText(promotion)}`,
        icon: Tag,
    }));
    const credit = promotions.credit.map((row) => {
        const chips = effectChips(row.effect);
        return {
            key: `credit-${row.promotionId}`,
            label: chips[0]?.short ?? row.name,
            title: `${row.name}: ${chips.map((chip) => chip.label).join(' · ') || row.name}`,
            icon: chips[0]?.icon ?? BadgePercent,
            tone: 'credit' as const,
        };
    });
    return [...sale, ...credit];
}
